import { CommonModule } from '@angular/common';
import { Component, ViewChild } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { DocumentType } from 'src/app/_enums/document-type.enum';
import { InsuranceFlowStatus } from 'src/app/_enums/insurance-flow/insurance-flow-status.enum';
import { ProposalState } from 'src/app/_enums/proposal.enums';
import { FileSizePipe } from 'src/app/_pipes/file-size.pipe';
import { ProposalService } from 'src/app/_services/proposal.service';
import { UserAuthService } from 'src/app/_services/user-auth.service';
import { ProposalDetails } from 'src/app/_types/proposals.type';
import { ActionModalComponent } from 'src/app/shared/components/action-modal/action-modal.component';
import { IconsModule } from 'src/app/shared/icons/icons.module';

import { TaskDetailsService } from '../../../../task-details.service';
import { BaseStepperAction } from '../base-action';

@Component({
  selector: 'app-create-policy-stepper-action',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgbTooltipModule,
    ActionModalComponent,
    FileSizePipe,
    IconsModule,
  ],
  templateUrl: './create-policy.component.html',
})
export class CreatePolicyStepperActionComponent extends BaseStepperAction {
  protected proposal: ProposalDetails | null = null;

  protected uploadedFiles: File[] = [];

  protected submitted = false;

  protected policyFormGroup = new FormGroup({
    policyId: new FormControl('', [Validators.required]),
    contractDoc: new FormControl(''),
  });

  @ViewChild('actionModal') private readonly actionModal?: ActionModalComponent;

  constructor(
    taskDetailsService: TaskDetailsService,
    private readonly userAuthService: UserAuthService,
    private readonly proposalService: ProposalService,
    private readonly toastrService: ToastrService
  ) {
    super(taskDetailsService);
  }

  override ngOnInit(): void {
    this.hydrateFromState();
    this.state = this.taskDetailsService.state;
    this.subscription = this.taskDetailsService.state$.subscribe((value) => {
      this.state = value;
      this.hydrateFromState();
    });
  }

  get enabled() {
    return (
      this.state?.insuranceFlowData?.status ===
      InsuranceFlowStatus.PENDING_VALIDATION
    );
  }

  protected get submitDisabled(): boolean {
    return (
      this.policyFormGroup.invalid ||
      this.uploadedFiles.length === 0 ||
      this.submitted
    );
  }

  protected openModal(): void {
    if (!this.proposal) {
      return;
    }

    this.actionModal?.openModal();
  }

  protected onFileUpload(event: Event): void {
    const selectedFiles: File[] = Array.from(
      (event.target as HTMLInputElement).files ?? []
    );
    selectedFiles.forEach((file) => {
      const alreadySelected = this.uploadedFiles.some(
        (existingFile) =>
          existingFile.type === file.type &&
          existingFile.size === file.size &&
          existingFile.name === file.name
      );
      if (!alreadySelected) {
        this.uploadedFiles.push(file);
      }
    });
  }

  protected removeFile(index: number): void {
    this.uploadedFiles.splice(index, 1);
  }

  protected addPolicy(): void {
    if (!this.proposal) {
      return;
    }

    if (this.policyFormGroup.invalid || this.uploadedFiles.length === 0) {
      return;
    }

    if (this.submitted) {
      return;
    }

    const { memberId } = this.proposal.memberDetails;
    const { familyMemberId } = this.proposal.familyMemberDetails;
    if (!memberId || !familyMemberId) {
      return;
    }

    this.submitted = true;
    this.userAuthService
      .uploadDocuments(
        this.uploadedFiles,
        memberId,
        familyMemberId,
        DocumentType.OTHER
      )
      .subscribe({
        next: (uploadedDocuments) => {
          const formdata = {
            proposalId: this.proposal?.proposalId,
            policyId: this.policyFormGroup.value.policyId,
            contractDoc: uploadedDocuments.map(
              (document) => document.documentId
            ),
          };

          this.proposalService._addPolicyId(formdata).subscribe({
            next: () => {
              this.toastrService.success($localize`Police créée avec succès`);
              this.taskDetailsService.refreshTaskDetails();
              this.resetFormState();
              this.actionModal?.closeModal();
            },
            error: () => {
              this.toastrService.error(
                $localize`Une erreur est survenue lors de la création de la police`
              );
              this.submitted = false;
            },
          });
        },
        error: () => {
          this.toastrService.error(
            $localize`Une erreur est survenue lors du téléchargement des documents`
          );
          this.submitted = false;
        },
      });
  }

  protected resetFormState(): void {
    this.uploadedFiles = [];
    this.policyFormGroup.reset();
    this.submitted = false;
  }

  private hydrateFromState(): void {
    const { policy, proposals } = this.taskDetailsService.state;
    if (policy?.proposalState === ProposalState.SIGNED_DOCS) {
      this.proposal = policy;
      return;
    }

    this.proposal =
      proposals.find(
        (proposal) => proposal.proposalState === ProposalState.SIGNED_DOCS
      ) ?? null;
  }
}
